import {
  DOCUMENT_URL_PATTERN,
  FB_REEL_TYPE,
  IMAGE_TYPES,
  REEL_PLATFORMS,
  SHORTS_PATTERN,
  SHORTS_PLATFORM,
  STORY_REEL_STORY_TYPES,
  VIDEO_TYPES,
  VIDEO_URL_PATTERN,
} from "./rules";

export const POST_CONTENT_TYPES = [
  "story",
  "reel",
  "text",
  "image",
  "video",
  "multiMedia",
  "document",
] as const;

export type PostContentType = (typeof POST_CONTENT_TYPES)[number];

/** The filter UI's "All post types" value. */
export const ALL_POST_CONTENT_TYPES = "*";

/** True when the selection filters nothing out. */
export function selectsEveryPostContentType(
  selected: readonly string[] | null | undefined
): boolean {
  return (
    !selected ||
    selected.length === 0 ||
    selected.includes(ALL_POST_CONTENT_TYPES) ||
    POST_CONTENT_TYPES.every((value) => selected.includes(value))
  );
}

/**
 * The stored post fields the classifier reads. `storyType` and `platformType`
 * are `unknown` because production holds junk objects in both.
 */
export interface ClassifiablePost {
  type?: string | null;
  storyType?: unknown;
  isStory?: boolean | null;
  isVideo?: boolean | null;
  isFbReel?: boolean | null;
  url?: string | null;
  platformType?: unknown;
  ytTitle?: string | null;
  caption?: string | null;
}

export interface ClassifyContext {
  /** Overrides post.platformType in classify(). */
  platform?: unknown;
  /** One platform per account, for classifyAll(). */
  platforms?: readonly unknown[] | null;
}

export type VideoPlatformKind = "reel" | "shorts" | "video";

/**
 * Upper-cased platform code, "IG" when missing or empty, or null when the
 * stored value is unusable (it is then treated as a non-IG platform).
 */
function resolvePlatform(platform: unknown): string | null {
  if (platform === undefined || platform === null || platform === "") {
    return "IG";
  }
  if (typeof platform === "string") return platform.toUpperCase();
  if (typeof platform === "object") {
    const nested = (platform as { platformType?: unknown }).platformType;
    if (typeof nested === "string" && nested !== "") {
      return nested.toUpperCase();
    }
  }
  return null;
}

/** How a video on this platform classifies: Reels, YouTube Shorts rule, or Single video. */
export function videoPlatformKind(platform: unknown): VideoPlatformKind {
  const resolved = resolvePlatform(platform);
  if (resolved === SHORTS_PLATFORM) return "shorts";
  if ((REEL_PLATFORMS as readonly (string | null)[]).includes(resolved)) {
    return "reel";
  }
  return "video";
}

function testString(pattern: RegExp, value: unknown) {
  return typeof value === "string" && pattern.test(value);
}

function includesValue(list: readonly string[], value: unknown) {
  return typeof value === "string" && list.includes(value);
}

export function classify(
  post: ClassifiablePost,
  ctx?: ClassifyContext
): PostContentType | null {
  const { type } = post;

  if (
    type === "story" &&
    includesValue(STORY_REEL_STORY_TYPES, post.storyType)
  ) {
    return "reel";
  }
  if (post.isStory === true || type === "story") return "story";
  if (type === "multiMedia") return "multiMedia";
  if (type === "text") return "text";
  if (type === "document" || testString(DOCUMENT_URL_PATTERN, post.url)) {
    return "document";
  }
  if (type === FB_REEL_TYPE || post.isFbReel === true) return "reel";
  if (
    includesValue(VIDEO_TYPES, type) ||
    post.isVideo === true ||
    testString(VIDEO_URL_PATTERN, post.url)
  ) {
    const kind = videoPlatformKind(ctx?.platform ?? post.platformType);
    if (kind === "reel") return "reel";
    if (
      kind === "shorts" &&
      (testString(SHORTS_PATTERN, post.ytTitle) ||
        testString(SHORTS_PATTERN, post.caption))
    ) {
      return "reel";
    }
    return "video";
  }
  if (includesValue(IMAGE_TYPES, type)) return "image";
  return null;
}

/**
 * Every value a multi-account post takes, one per account platform, in first
 * seen order. Falls back to classify(post, ctx) when no platforms are given.
 */
export function classifyAll(
  post: ClassifiablePost,
  ctx?: ClassifyContext
): (PostContentType | null)[] {
  const platforms = ctx?.platforms;
  if (!platforms || platforms.length === 0) return [classify(post, ctx)];

  const values: (PostContentType | null)[] = [];
  for (const platform of platforms) {
    const value = classify(post, { platform: platform ?? "" });
    if (!values.includes(value)) values.push(value);
  }
  return values;
}

/** The server's stamp when there is one (null means unknown), else classifyAll(). */
export function resolvePostContentTypes(
  post: ClassifiablePost & { postContentType?: PostContentType | null },
  ctx?: ClassifyContext
): (PostContentType | null)[] {
  return post.postContentType !== undefined
    ? [post.postContentType]
    : classifyAll(post, ctx);
}

/**
 * An empty or absent selection matches everything, and an unknown (null)
 * value always matches, so a post the classifier cannot place is never hidden.
 */
export function matchesPostContentTypes(
  values: PostContentType | null | readonly (PostContentType | null)[],
  selected: readonly string[] | null | undefined
): boolean {
  if (!selected || selectsEveryPostContentType(selected)) return true;
  const list = Array.isArray(values) ? values : [values];
  if (list.length === 0) return true;
  return list.some((value) => value === null || selected.includes(value));
}
